import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { useInvoicesQuery } from "./__generated__/useFetchPatientInvoices.gql";

const AUTOMATED_INVOICE_CREATOR_ID = "f4d125b6-80ee-4cd6-8d86-b9878c5dc349";

export const QUERY = gql`
  query invoices($patientUuid: String!) {
    invoices(patientUuid: $patientUuid) {
      _id
      created
      invoiceNumber
      invoiceStatus
      patientType
      createdBy {
        person {
          firstname
          lastname
        }
        uuid
      }
      payers {
        type
        balance {
          paid
        }
      }
      balance {
        total
        paid
        due
        currency
      }
      facility {
        name
        uuid
      }
      organisation {
        uuid
        name
      }
    }
  }
`;

export function useFetchPatientInvoices(patientId: string) {
  const { formatMessage } = useIntl();

  const { loading, error, data } = useInvoicesQuery({
    fetchPolicy: "network-only",
    variables: {
      patientUuid: patientId,
    },
    onError() {
      toast.error(
        formatMessage({
          id: "invoicing.getPatientInvoices.error.msg",
          defaultMessage: "Failed to fetch this patient's invoices",
        }),
      );
    },
  });

  const invoices = useMemo(() => {
    return (data?.invoices ?? [])
      .map((invoice) => {
        // `createdBy` is nullable in the federation schema and real-world
        // invoices on acceptance carry rows where the creator user has been
        // deleted — guard the whole chain so the list doesn't crash the
        // whole tab.
        const invoiceCreator =
          invoice.createdBy?.uuid === AUTOMATED_INVOICE_CREATOR_ID
            ? "Automated Invoice"
            : `${invoice.createdBy?.person?.firstname ?? ""} ${
                invoice.createdBy?.person?.lastname ?? ""
              }`.trim();
        return {
          invoiceId: invoice._id.toString(),
          invoiceSummary: {
            created: invoice.created,
            invoiceNumber: invoice.invoiceNumber,
            invoiceStatus: invoice.invoiceStatus,
            patientType: invoice.patientType,
            creator: invoiceCreator,
            payers: invoice.payers,
            balance: invoice.balance,
            facility: { ...invoice.facility, id: invoice.facility.uuid },
            organisation: {
              ...invoice.organisation,
              id: invoice.organisation.uuid,
            },
          },
        };
      })
      .sort(
        (a, b) =>
          new Date(b.invoiceSummary.created).valueOf() -
          new Date(a.invoiceSummary.created).valueOf(),
      );
  }, [data]);

  return { loading, error, invoices };
}
